import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Send, Copy, Download, RefreshCw, Loader2, 
  Check, AlertCircle, Upload, X, Play, Pause, Volume2,
  Settings, Sparkles, Image as ImageIcon, FileText, Code,
  Mic, Video, Bot, Share2, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import useEntitlements, { AI_TOOL_ADDON_IDS, moduleForAiTool } from '../hooks/useEntitlements';

const TOOL_CONFIG = {
  'writer': {
    name: 'AI Writer',
    description: 'Generate blog posts, articles, and content',
    icon: '✍️',
    category: 'content',
    fields: [
      { name: 'topic', label: 'Topic/Subject', type: 'text', placeholder: 'e.g., Benefits of Remote Work', required: true },
      { name: 'content_type', label: 'Content Type', type: 'select', options: ['blog_post', 'article', 'social_media', 'email', 'newsletter'] },
      { name: 'tone', label: 'Tone', type: 'select', options: ['professional', 'casual', 'friendly', 'formal', 'humorous', 'persuasive'] },
      { name: 'length', label: 'Length', type: 'select', options: ['short', 'medium', 'long'] },
      { name: 'keywords', label: 'Keywords (comma separated)', type: 'text', placeholder: 'SEO, productivity, tips' }
    ],
    resultType: 'markdown'
  },
  'editor': {
    name: 'AI Editor',
    description: 'Edit and improve your existing content',
    icon: '📝',
    category: 'content',
    fields: [
      { name: 'content', label: 'Your Content', type: 'textarea', placeholder: 'Paste your content here...', required: true },
      { name: 'action', label: 'Action', type: 'select', options: ['improve', 'simplify', 'expand', 'summarize', 'grammar', 'tone_shift'] }
    ],
    resultType: 'markdown'
  },
  'rewriter': {
    name: 'AI ReWriter',
    description: 'Rewrite content in different styles',
    icon: '🔄',
    category: 'content',
    fields: [
      { name: 'content', label: 'Original Content', type: 'textarea', placeholder: 'Paste content to rewrite...', required: true },
      { name: 'style', label: 'Target Style', type: 'select', options: ['professional', 'casual', 'formal', 'creative', 'persuasive', 'academic'] }
    ],
    resultType: 'markdown'
  },
  'article-wizard': {
    name: 'AI Article Wizard',
    description: 'Generate full articles with structure',
    icon: '📰',
    category: 'content',
    fields: [
      { name: 'topic', label: 'Article Topic', type: 'text', placeholder: 'e.g., Complete Guide to Digital Marketing', required: true },
      { name: 'sections', label: 'Number of Sections', type: 'number', min: 3, max: 10, default: 5 },
      { name: 'include_intro', label: 'Include Introduction', type: 'checkbox', default: true },
      { name: 'include_conclusion', label: 'Include Conclusion', type: 'checkbox', default: true }
    ],
    resultType: 'article'
  },
  'chat': {
    name: 'AI Chat',
    description: 'Chat with AI assistant',
    icon: '💬',
    category: 'chat',
    fields: [],
    resultType: 'chat'
  },
  'code': {
    name: 'AI Code',
    description: 'Generate and explain code',
    icon: '👨‍💻',
    category: 'chat',
    fields: [
      { name: 'prompt', label: 'What do you need?', type: 'textarea', placeholder: 'Describe the code you need...', required: true },
      { name: 'language', label: 'Language', type: 'select', options: ['python', 'javascript', 'typescript', 'java', 'c++', 'go', 'rust', 'sql', 'html', 'css'] },
      { name: 'action', label: 'Action', type: 'select', options: ['generate', 'explain', 'debug', 'optimize', 'convert'] },
      { name: 'code', label: 'Existing Code (optional)', type: 'code', placeholder: '// Paste code here for explain/debug/optimize' }
    ],
    resultType: 'code'
  },
  'image': {
    name: 'AI Image',
    description: 'Generate images from text',
    icon: '🎨',
    category: 'media',
    fields: [
      { name: 'prompt', label: 'Image Description', type: 'textarea', placeholder: 'A serene mountain landscape at sunset with golden light...', required: true },
      { name: 'style', label: 'Style', type: 'select', options: ['realistic', 'illustration', 'cartoon', '3d', 'watercolor', 'oil_painting', 'digital_art', 'anime'] },
      { name: 'size', label: 'Size', type: 'select', options: ['1024x1024', '1792x1024', '1024x1792'] },
      { name: 'num_images', label: 'Number of Images', type: 'number', min: 1, max: 4, default: 1 }
    ],
    resultType: 'images'
  },
  'photo-studio': {
    name: 'AI Photo Studio',
    description: 'Edit and enhance photos',
    icon: '📸',
    category: 'media',
    fields: [
      { name: 'image_url', label: 'Image URL', type: 'text', placeholder: 'https://... or choose/upload below', required: false },
      { name: 'action', label: 'Action', type: 'select', options: ['enhance', 'remove_bg', 'upscale', 'colorize', 'restore', 'retouch'] }
    ],
    resultType: 'image'
  },
  'chat-image': {
    name: 'AI Chat Image',
    description: 'Generate images through chat',
    icon: '🖼️',
    category: 'media',
    fields: [],
    resultType: 'chat-image'
  },
  'product-photo': {
    name: 'AI Product Photography',
    description: 'Create product photos',
    icon: '📦',
    category: 'media',
    fields: [
      { name: 'product_description', label: 'Product Description', type: 'textarea', placeholder: 'Describe your product...', required: true },
      { name: 'background', label: 'Background', type: 'select', options: ['white', 'gradient', 'studio', 'lifestyle', 'outdoor', 'custom'] },
      { name: 'angle', label: 'Angle', type: 'select', options: ['front', 'side', '45_degree', 'top', 'lifestyle'] },
      { name: 'lighting', label: 'Lighting', type: 'select', options: ['studio', 'natural', 'dramatic', 'soft', 'backlit'] }
    ],
    resultType: 'images'
  },
  'avatar': {
    name: 'AI Avatar',
    description: 'Generate AI avatars',
    icon: '👤',
    category: 'media',
    fields: [
      { name: 'description', label: 'Avatar Description', type: 'textarea', placeholder: 'Professional business person, friendly smile...', required: true },
      { name: 'style', label: 'Style', type: 'select', options: ['realistic', 'cartoon', 'anime', '3d', 'pixel_art', 'minimalist'] },
      { name: 'gender', label: 'Gender (optional)', type: 'select', options: ['', 'male', 'female', 'neutral'] }
    ],
    resultType: 'image'
  },
  'vision': {
    name: 'AI Vision',
    description: 'Analyze and describe images',
    icon: '👁️',
    category: 'media',
    fields: [
      { name: 'image_url', label: 'Image URL', type: 'text', placeholder: 'https://... or choose/upload below', required: false },
      { name: 'action', label: 'Analysis Type', type: 'select', options: ['describe', 'analyze', 'extract_text', 'detect_objects', 'sentiment'] }
    ],
    resultType: 'analysis'
  },
  'voiceover': {
    name: 'AI Voiceover',
    description: 'Generate voice narration',
    icon: '🎙️',
    category: 'audio',
    fields: [
      { name: 'text', label: 'Text to Speak', type: 'textarea', placeholder: 'Enter the text you want to convert to speech...', required: true },
      { name: 'voice', label: 'Voice', type: 'select', options: ['alloy', 'echo', 'fable', 'nova', 'onyx', 'shimmer'] },
      { name: 'speed', label: 'Speed', type: 'range', min: 0.5, max: 2, step: 0.1, default: 1 },
      { name: 'language', label: 'Language', type: 'select', options: ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'zh', 'hi', 'ar', 'ru'] }
    ],
    resultType: 'audio'
  },
  'speech-to-text': {
    name: 'AI Speech to Text',
    description: 'Transcribe audio to text',
    icon: '📜',
    category: 'audio',
    fields: [
      { name: 'audio_url', label: 'Audio URL', type: 'text', placeholder: 'https://... or choose/upload below', required: false },
      { name: 'language', label: 'Language', type: 'select', options: ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'zh', 'auto'] }
    ],
    resultType: 'transcript'
  },
  'detector': {
    name: 'AI Detector',
    description: 'Detect AI-generated content',
    icon: '🔍',
    category: 'analysis',
    fields: [
      { name: 'content', label: 'Content to Analyze', type: 'textarea', placeholder: 'Paste content to check if AI-generated...', required: true }
    ],
    resultType: 'detection'
  },
  'plagiarism': {
    name: 'AI Plagiarism',
    description: 'Check content for plagiarism',
    icon: '✅',
    category: 'analysis',
    fields: [
      { name: 'content', label: 'Content to Check', type: 'textarea', placeholder: 'Paste content to check for plagiarism...', required: true }
    ],
    resultType: 'plagiarism'
  },
  'video': {
    name: 'AI Video Generator',
    description: 'Generate AI videos from text',
    icon: '🎬',
    category: 'video',
    fields: [
      { name: 'prompt', label: 'Video Description', type: 'textarea', placeholder: 'A drone flying over mountains at sunset...', required: true },
      { name: 'duration', label: 'Duration (seconds)', type: 'select', options: ['4', '8', '12'], default: '4' },
      { name: 'style', label: 'Style', type: 'select', options: ['realistic', 'cinematic', 'animation', 'abstract', 'documentary'] },
      { name: 'size', label: 'Aspect Ratio', type: 'select', options: ['1280x720', '1024x1792', '1024x1024'], default: '1280x720' }
    ],
    resultType: 'video'
  },
  'youtube': {
    name: 'AI YouTube',
    description: 'YouTube content optimization',
    icon: '▶️',
    category: 'video',
    fields: [
      { name: 'video_topic', label: 'Video Topic', type: 'text', placeholder: 'e.g., How to Start a Business in 2025', required: true },
      { name: 'action', label: 'Generate', type: 'select', options: ['title', 'description', 'tags', 'script', 'thumbnail_idea', 'all'] }
    ],
    resultType: 'youtube'
  },
  'file-chat': {
    name: 'AI File Chat',
    description: 'Chat with your documents',
    icon: '📄',
    category: 'documents',
    fields: [
      { name: 'file_url', label: 'Document URL', type: 'text', placeholder: 'https://... or choose/upload below (PDF, DOCX, TXT)', required: false },
      { name: 'file_type', label: 'File Type', type: 'select', options: ['pdf', 'docx', 'txt', 'csv', 'xlsx'] }
    ],
    resultType: 'file-chat'
  },
  'web-chat': {
    name: 'AI Web Chat',
    description: 'Research and chat about websites',
    icon: '🌐',
    category: 'documents',
    fields: [
      { name: 'url', label: 'Website URL', type: 'text', placeholder: 'https://example.com', required: true }
    ],
    resultType: 'web-chat'
  },
  'rss': {
    name: 'AI RSS',
    description: 'Generate content from RSS feeds',
    icon: '📡',
    category: 'documents',
    fields: [
      { name: 'feed_url', label: 'RSS Feed URL', type: 'text', placeholder: 'https://example.com/feed.xml', required: true },
      { name: 'content_type', label: 'Content Type', type: 'select', options: ['summary', 'article', 'social_post', 'newsletter'] },
      { name: 'num_items', label: 'Number of Items', type: 'number', min: 1, max: 20, default: 5 }
    ],
    resultType: 'rss'
  },
  'presentation': {
    name: 'AI Presentation',
    description: 'Generate slide presentations',
    icon: '📊',
    category: 'documents',
    fields: [
      { name: 'topic', label: 'Presentation Topic', type: 'text', placeholder: 'e.g., Q4 2025 Marketing Strategy', required: true },
      { name: 'num_slides', label: 'Number of Slides', type: 'number', min: 5, max: 30, default: 10 },
      { name: 'style', label: 'Style', type: 'select', options: ['professional', 'creative', 'minimal', 'corporate', 'educational'] },
      { name: 'include_images', label: 'Include Images', type: 'checkbox', default: true }
    ],
    resultType: 'presentation'
  },
  'smart-bot': {
    name: 'AI Smart Bots',
    description: 'Create custom chatbots',
    icon: '🤖',
    category: 'bots',
    fields: [
      { name: 'name', label: 'Bot Name', type: 'text', placeholder: 'e.g., SupportBot', required: true },
      { name: 'description', label: 'Bot Description', type: 'textarea', placeholder: 'What should this bot do?', required: true },
      { name: 'personality', label: 'Personality', type: 'select', options: ['helpful', 'friendly', 'professional', 'casual', 'formal'] },
      { name: 'system_prompt', label: 'System Prompt', type: 'textarea', placeholder: 'Optional: instructions/personality for this bot (how it should respond, what to focus on)...', required: false }
    ],
    resultType: 'bot'
  },
  'voice-bot': {
    name: 'AI Voice Bot',
    description: 'Build voice chatbots',
    icon: '📞',
    category: 'bots',
    fields: [
      { name: 'name', label: 'Bot Name', type: 'text', placeholder: 'e.g., VoiceAssistant', required: true },
      { name: 'description', label: 'Bot Purpose', type: 'textarea', placeholder: 'What should this voice bot do?', required: true },
      { name: 'voice', label: 'Voice', type: 'select', options: ['neutral', 'male', 'female'] },
      { name: 'language', label: 'Language', type: 'select', options: ['en', 'es', 'fr', 'de'] }
    ],
    resultType: 'voice-bot'
  },
  'social-media': {
    name: 'AI Social Media',
    description: 'Manage social media',
    icon: '📱',
    category: 'marketing',
    fields: [
      { name: 'topic', label: 'Topic/Product', type: 'text', placeholder: 'e.g., New product launch', required: true },
      { name: 'platform', label: 'Platform', type: 'select', options: ['twitter', 'facebook', 'instagram', 'linkedin', 'tiktok'] },
      { name: 'content_type', label: 'Content Type', type: 'select', options: ['post', 'story', 'caption', 'hashtags', 'thread'] }
    ],
    resultType: 'social'
  },
  'brand-voice': {
    name: 'Brand Voice',
    description: 'Define your brand tone',
    icon: '🎯',
    category: 'marketing',
    fields: [
      { name: 'brand_name', label: 'Brand Name', type: 'text', placeholder: 'Your Brand', required: true },
      { name: 'industry', label: 'Industry', type: 'text', placeholder: 'e.g., Technology, Fashion, Healthcare' },
      { name: 'target_audience', label: 'Target Audience', type: 'text', placeholder: 'e.g., Young professionals, Parents' },
      { name: 'tone', label: 'Desired Tone', type: 'select', options: ['professional', 'casual', 'friendly', 'authoritative', 'playful', 'luxurious'] },
      { name: 'values', label: 'Brand Values (comma separated)', type: 'text', placeholder: 'Innovation, Trust, Quality' }
    ],
    resultType: 'brand-voice'
  }
};

const AIToolPage = () => {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { can, entitlements, loading: entitlementsLoading } = useEntitlements();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [history, setHistory] = useState([]);
  const [photoStudioUploadLoading, setPhotoStudioUploadLoading] = useState(false);
  const [audioUploadLoading, setAudioUploadLoading] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);

  const requiredModule = moduleForAiTool(toolId);
  const isAddonLocked = Boolean(
    entitlements
    && !entitlementsLoading
    && AI_TOOL_ADDON_IDS.has(requiredModule)
    && !can(requiredModule)
  );

  const uploadDocumentFile = async (file) => {
    if (!file) return;
    const token = localStorage.getItem('token');
    setFileUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/upload-file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to upload file');
      }
      const media = await res.json();
      if (media.url) {
        handleInputChange('file_url', media.url);
        toast.success('Document uploaded');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setFileUploadLoading(false);
    }
  };

  const token = localStorage.getItem('token');

  const loadHistory = useCallback(async () => {
    if (!token || !toolId) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/history/${toolId}?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const items = await res.json();
        setHistory(items || []);
      }
    } catch (err) {
      console.error('Failed to load history', err);
    }
  }, [token, toolId]);

  const uploadPhotoStudioImage = async (file) => {
    if (!file) return;
    const token = localStorage.getItem('token');
    setPhotoStudioUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to upload image');
      }
      const media = await res.json();
      if (media.url) {
        handleInputChange('image_url', media.url);
        toast.success('Image uploaded');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setPhotoStudioUploadLoading(false);
    }
  };

  const uploadAudioFile = async (file) => {
    if (!file) return;
    const token = localStorage.getItem('token');
    setAudioUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/upload-audio`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to upload audio');
      }
      const media = await res.json();
      if (media.url) {
        handleInputChange('audio_url', media.url);
        toast.success('Audio uploaded');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to upload audio');
    } finally {
      setAudioUploadLoading(false);
    }
  };

  const messagesEndRef = useRef(null);
  
  const tool = TOOL_CONFIG[toolId];

  // Redirect deprecated tools to Media Studio
  useEffect(() => {
    if (toolId === 'image' || toolId === 'video' || toolId === 'photo-studio') {
      navigate('/media-studio', { replace: true });
      return;
    }
    if (toolId === 'presentation') {
      navigate('/ai-presentation', { replace: true });
      return;
    }
    if (toolId === 'social-media') {
      navigate('/marketing-suite/ads', { replace: true });
      return;
    }
    if (!tool) {
      toast.error('Tool not found');
      navigate('/ai-tools-dashboard');
    }
    // Initialize default values
    if (tool) {
      const defaults = {};
      tool.fields.forEach(field => {
        if (field.default !== undefined) {
          defaults[field.name] = field.default;
        }
      });
      setFormData(defaults);
      loadHistory();
    }
  }, [toolId, tool, navigate, loadHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isAddonLocked) {
      toast.message(`${tool?.name || 'This tool'} is a premium add-on`, {
        description: 'Buy it from Snagcart Modules to unlock.',
      });
      navigate('/snagcart/apps', { state: { highlightAddon: requiredModule } });
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/${toolId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Detect backends that return 200 but with an embedded "failure" message
        // (e.g. empty images array + error message). Surface it instead of
        // showing a misleading "Generated successfully" toast.
        const isImageTool = ['image', 'photo-studio', 'product-photo', 'avatar', 'chat-image'].includes(toolId);
        const hasNoImage = isImageTool && (!data.images || data.images.length === 0) && !data.image_url && !data.processed_image_url;
        const isVoice = toolId === 'voiceover' && !data.audio_url;
        const isVideo = toolId === 'video' && !data.video_url;
        const isSTT = toolId === 'speech-to-text' && !data.transcription;

        if (hasNoImage || isVoice || isVideo || isSTT) {
          setResult(data);  // still set so the message renders in the result panel
          toast.error(data.message || 'Generation produced no output. Check your settings or API key.');
          loadHistory();
        } else {
          setResult(data);
          toast.success('Generated successfully!');
          loadHistory();
        }
      } else {
        const detail = data.detail;
        if (response.status === 403 && detail?.code === 'module_locked') {
          toast.message(detail.message || 'This tool is locked', {
            description: 'Buy the add-on from Snagcart Modules.',
          });
          navigate('/snagcart/apps', { state: { highlightAddon: detail.module_id || requiredModule } });
        } else {
          toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Generation failed'));
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setLoading(true);

    try {
      const endpoint = toolId === 'chat-image' ? 'chat-image' : 
                      toolId === 'file-chat' ? 'file-chat' :
                      toolId === 'web-chat' ? 'web-chat' : 'chat';
      
      const body = { message: chatInput, ...formData };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-tools/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      const assistantMessage = { 
        role: 'assistant', 
        content: data.response,
        image: data.generated_image?.url,
        mcp: data._mcp
      };
      setChatMessages(prev => [...prev, assistantMessage]);
      loadHistory();
    } catch (error) {
      toast.error('Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadResult = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  if (!tool) return null;

  const isChatMode = ['chat', 'chat-image', 'file-chat', 'web-chat'].includes(toolId);

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="bg-zinc-800 border-zinc-700"
            required={field.required}
          />
        );
      case 'textarea':
      case 'code':
        return (
          <textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full h-32 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 ${field.type === 'code' ? 'font-mono text-sm' : ''}`}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={formData[field.name] || field.options[0]}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={formData[field.name] || field.default || ''}
            onChange={(e) => handleInputChange(field.name, parseInt(e.target.value))}
            min={field.min}
            max={field.max}
            className="bg-zinc-800 border-zinc-700"
          />
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData[field.name] !== false}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-violet-500 focus:ring-violet-500"
            />
            <span className="text-sm text-zinc-400">Enable</span>
          </label>
        );
      case 'range':
        return (
          <div className="flex items-center gap-4">
            <input
              type="range"
              value={formData[field.name] || field.default}
              onChange={(e) => handleInputChange(field.name, parseFloat(e.target.value))}
              min={field.min}
              max={field.max}
              step={field.step}
              className="flex-1"
            />
            <span className="text-sm text-zinc-400 w-12">{formData[field.name] || field.default}x</span>
          </div>
        );
      default:
        return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;

    switch (tool.resultType) {
      case 'markdown':
        return (
          <div className="prose prose-invert max-w-none">
            <div className="bg-zinc-800 rounded-xl p-6 whitespace-pre-wrap">{result.content || result.improved_content || result.rewritten_content}</div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => copyToClipboard(result.content || result.improved_content || result.rewritten_content)} variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
              <Button onClick={() => downloadResult(result.content || result.improved_content || result.rewritten_content, `${toolId}-result.txt`)} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
          </div>
        );
      case 'article':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">{result.article?.title}</h2>
            {result.article?.sections?.map((section, i) => (
              <div key={i} className="bg-zinc-800 rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-2">{section.heading}</h3>
                <p className="text-zinc-300">{section.content}</p>
              </div>
            ))}
            <p className="text-sm text-zinc-500">Word count: {result.word_count}</p>
          </div>
        );
      case 'code':
        return (
          <div className="space-y-4">
            <div className="bg-zinc-900 rounded-xl p-4 font-mono text-sm overflow-x-auto">
              <pre className="text-green-400">{result.code}</pre>
            </div>
            {result.explanation && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-zinc-300">{result.explanation}</p>
              </div>
            )}
            <Button onClick={() => copyToClipboard(result.code)} variant="outline" size="sm">
              <Copy className="w-4 h-4 mr-2" /> Copy Code
            </Button>
          </div>
        );
      case 'images':
      case 'image': {
        const resolveUrl = (u) => u && u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
        let images = result.images || (result.url ? [result] : []);
        
        // Handle photo-studio case where backend might only return processed_image_url
        if (images.length === 0 && result.processed_image_url) {
            images = [{ url: result.processed_image_url }];
        }

        if (images.length === 0) {
          return (
            <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/40 text-sm space-y-2">
              <p className="font-semibold text-red-300">Image generation failed</p>
              <p className="text-red-200 whitespace-pre-wrap break-words">
                {result.message || 'No images were generated. Check your API key in Settings → API Keys.'}
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <div className={`grid ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              {images.map((img, i) => {
                const imgSrc = resolveUrl(img.url || img.avatar_url || img.processed_image_url);
                return (
                  <div key={i} className="relative group">
                    <img src={imgSrc} alt="Generated" className="w-full rounded-xl" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          if (!imgSrc) return;
                          const a = document.createElement('a');
                          a.href = imgSrc;
                          a.download = `${toolId}-image-${i + 1}.png`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      case 'transcript':
        return (
          <div className="space-y-4">
            {result.transcription ? (
              <>
                <div className="bg-zinc-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-zinc-300">Transcription</p>
                    {result.language && (
                      <span className="text-xs px-2 py-1 bg-zinc-700 rounded-full text-zinc-400">
                        {result.language.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-zinc-100 leading-relaxed">{result.transcription}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => copyToClipboard(result.transcription)} variant="outline" size="sm">
                    <Copy className="w-4 h-4 mr-2" /> Copy Text
                  </Button>
                  <Button onClick={() => downloadResult(result.transcription, 'transcription.txt')} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" /> Download TXT
                  </Button>
                </div>
              </>
            ) : (
              <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/40 text-sm">
                <p className="font-semibold text-red-300">Transcription failed</p>
                <p className="text-red-200 mt-1">{result.message || 'No transcription returned.'}</p>
              </div>
            )}
          </div>
        );
      case 'audio': {
        let audioSrc = result.audio_url || '';
        if (audioSrc.startsWith('/api/')) {
          audioSrc = `${process.env.REACT_APP_BACKEND_URL}${audioSrc}`;
        }
        return (
          <div className="space-y-4">
            {audioSrc ? (
              <audio controls className="w-full" src={audioSrc}>
                Your browser does not support audio.
              </audio>
            ) : (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/40 text-sm">
                <p className="font-semibold text-red-300">Audio generation failed</p>
                <p className="text-red-200 mt-1">{result.message || 'No audio returned.'}</p>
              </div>
            )}
            {result.voice && <p className="text-sm text-zinc-500">Voice: <span className="capitalize text-zinc-400">{result.voice}</span></p>}
          </div>
        );
      }
      case 'video':
        // Support both MOCK structure (thumbnail_url/status) and REAL video_url
        // Also remap old history entries pointing to sample-videos.com to a local sample file
        let videoUrl = result.video_url;
        if (videoUrl && videoUrl.startsWith('https://sample-videos.com')) {
          videoUrl = '/api/media/ai-tools/video/shared/f3ccd71d-8457-48bb-8b45-b0ae8c6722dd.mp4';
        }
        if (videoUrl && videoUrl.startsWith('/api/')) {
          videoUrl = `${process.env.REACT_APP_BACKEND_URL}${videoUrl}`;
        }
        if (videoUrl) {
          return (
            <div className="space-y-4">
              <div className="relative aspect-video bg-zinc-800 rounded-xl overflow-hidden">
                <video
                  controls
                  className="w-full h-full object-cover"
                  src={videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex items-center justify-between text-sm text-zinc-500">
                <span>
                  Duration: {result.duration || 0}s{result.model ? ` | Model: ${result.model}` : ''}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = videoUrl;
                    a.download = `${toolId}-video.mp4`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <div className="relative aspect-video bg-zinc-800 rounded-xl overflow-hidden">
              <img src={result.thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-violet-500 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
              </div>
            </div>
            <p className="text-sm text-zinc-500">Duration: {result.duration}s | Status: {result.status}</p>
          </div>
        );
      case 'detection':
        return (
          <div className="space-y-4">
            <div className={`p-6 rounded-xl ${result.is_ai_generated ? 'bg-red-500/20 border border-red-500/50' : 'bg-green-500/20 border border-green-500/50'}`}>
              <div className="flex items-center gap-3 mb-4">
                {result.is_ai_generated ? (
                  <AlertCircle className="w-8 h-8 text-red-400" />
                ) : (
                  <Check className="w-8 h-8 text-green-400" />
                )}
                <div>
                  <p className="font-semibold text-lg">{result.is_ai_generated ? 'Likely AI Generated' : 'Likely Human Written'}</p>
                  <p className="text-sm text-zinc-400">Confidence: {(result.confidence * 100).toFixed(0)}%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 p-4 rounded-lg">
                  <p className="text-sm text-zinc-400">Human Written</p>
                  <p className="text-2xl font-bold text-green-400">{(result.human_written_probability * 100).toFixed(0)}%</p>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg">
                  <p className="text-sm text-zinc-400">AI Written</p>
                  <p className="text-2xl font-bold text-red-400">{(result.ai_written_probability * 100).toFixed(0)}%</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'plagiarism':
        return (
          <div className={`p-6 rounded-xl ${result.is_original ? 'bg-green-500/20 border border-green-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
            <div className="flex items-center gap-3">
              {result.is_original ? (
                <Check className="w-8 h-8 text-green-400" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-400" />
              )}
              <div>
                <p className="font-semibold text-lg">{result.is_original ? 'Original Content' : 'Potential Plagiarism Detected'}</p>
                <p className="text-sm text-zinc-400">Originality score: {result.originality_score}%</p>
              </div>
            </div>
            {result.matches?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-semibold">Matched Sources:</p>
                <ul className="list-disc list-inside text-sm text-zinc-300">
                  {result.matches.map((match, i) => (
                    <li key={i}>{match}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.message && (
              <p className="mt-4 text-sm text-zinc-400">{result.message}</p>
            )}
          </div>
        );
      case 'analysis':
        return (
          <div className="space-y-3">
            {result.description && (
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{result.description}</p>
            )}
            {Array.isArray(result.objects_detected) && result.objects_detected.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-400 mb-1">Objects detected:</p>
                <p className="text-sm text-zinc-200">{result.objects_detected.join(', ')}</p>
              </div>
            )}
            {result.text_extracted && (
              <div>
                <p className="text-xs font-semibold text-zinc-400 mb-1">Extracted text:</p>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{result.text_extracted}</p>
              </div>
            )}
            {typeof result.confidence === 'number' && (
              <p className="text-xs text-zinc-500">Model confidence: {(result.confidence * 100).toFixed(1)}%</p>
            )}
          </div>
        );
      case 'youtube':
        return (
          <div className="space-y-4">
            {result.titles && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-sm text-zinc-400 mb-2">Title Suggestions</p>
                {result.titles.map((title, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-700 last:border-0">
                    <p>{title}</p>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(title)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {result.description && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-zinc-400">Description</p>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.description)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="whitespace-pre-wrap text-sm">{result.description}</p>
              </div>
            )}
            {result.script && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-zinc-400">Video Script</p>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.script)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{result.script}</p>
              </div>
            )}
            {result.thumbnail_idea && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-sm text-zinc-400 mb-2">Thumbnail Idea</p>
                <p className="text-sm">{result.thumbnail_idea}</p>
              </div>
            )}
            {result.tags && result.tags.length > 0 && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-zinc-400">Tags</p>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.tags.join(', '))}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-violet-500/20 text-violet-400 rounded-full text-sm">{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case 'presentation':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-zinc-300">Slide preview</h3>
              {result.pptx_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = result.pptx_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${result.pptx_url}` : result.pptx_url;
                    a.download = `${toolId}-presentation.pptx`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  <Download className="w-4 h-4 mr-2" /> Download PPTX
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {result.slides?.map((slide, i) => (
                <div key={i} className="bg-zinc-800 rounded-xl p-4 aspect-video flex flex-col">
                  <p className="text-xs text-zinc-500 mb-1">Slide {slide.slide_number}</p>
                  <p className="font-semibold text-sm">{slide.title}</p>
                  <p className="text-xs text-zinc-400 mt-auto truncate">{slide.content}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'bot':
      case 'voice-bot':
        return (
          <div className="bg-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-violet-500 flex items-center justify-center">
                <Bot className="w-8 h-8" />
              </div>
              <div>
                <p className="font-semibold text-lg">{result.name}</p>
                <p className="text-sm text-zinc-400">Status: {result.status}</p>
              </div>
            </div>
            {result.embed_code && (
              <div className="mt-4">
                <p className="text-sm text-zinc-400 mb-2">Embed Code</p>
                <div className="bg-zinc-900 rounded-lg p-3 font-mono text-xs">
                  {result.embed_code}
                </div>
              </div>
            )}
          </div>
        );
      case 'social':
        return (
          <div className="space-y-4">
            {result.posts?.map((post, i) => (
              <div key={i} className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-violet-500/20 text-violet-400 rounded text-xs capitalize">{post.platform}</span>
                  <span className="text-xs text-zinc-500">Best time: {post.best_time}</span>
                </div>
                <p className="mb-3">{post.content}</p>
                <div className="flex flex-wrap gap-2">
                  {post.hashtags?.map((tag, j) => (
                    <span key={j} className="text-violet-400 text-sm">{tag}</span>
                  ))}
                </div>
                <Button size="sm" className="mt-3" onClick={() => copyToClipboard(post.content + ' ' + post.hashtags?.join(' '))}>
                  <Copy className="w-4 h-4 mr-2" /> Copy Post
                </Button>
              </div>
            ))}
          </div>
        );
      case 'brand-voice':
        return (
          <div className="space-y-4">
            <div className="bg-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4">{result.brand_voice_profile?.name} Brand Voice</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-400 mb-2">Personality Traits</p>
                  <div className="flex flex-wrap gap-2">
                    {result.brand_voice_profile?.personality_traits?.map((trait, i) => (
                      <span key={i} className="px-3 py-1 bg-violet-500/20 text-violet-400 rounded-full text-sm capitalize">{trait}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-zinc-400 mb-2">Tone</p>
                  <p className="capitalize">{result.brand_voice_profile?.tone}</p>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-zinc-400 mb-2">Sample Messages</p>
                {Object.entries(result.brand_voice_profile?.sample_messages || {}).map(([key, value]) => (
                  <div key={key} className="bg-zinc-900 rounded-lg p-3 mb-2">
                    <p className="text-xs text-zinc-500 capitalize mb-1">{key}</p>
                    <p>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-zinc-800 rounded-xl p-6">
            <pre className="text-sm whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4 sticky top-0 bg-zinc-950/90 backdrop-blur-xl z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/ai-tools-dashboard')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Tools
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-3xl">{tool.icon}</span>
              <div>
                <h1 className="text-xl font-bold">{tool.name}</h1>
                <p className="text-xs text-zinc-500">{tool.description}</p>
              </div>
              {isAddonLocked && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-1 rounded-full">
                  <Lock className="w-3 h-3" /> Add-on required
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full">
            <Sparkles className="w-3 h-3" />
            Powered by AI
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {isAddonLocked && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-100">{tool.name} is a premium add-on</p>
                <p className="text-sm text-amber-200/70 mt-0.5">
                  It is not included in your plan. Purchase it from Snagcart Modules to unlock.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/snagcart/apps', { state: { highlightAddon: requiredModule } })}
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold shrink-0"
            >
              View add-on
            </Button>
          </div>
        )}
        {isChatMode ? (
          // Chat Interface
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Settings Panel */}
            {tool.fields.length > 0 && (
              <div className="lg:col-span-1">
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 sticky top-24">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Settings className="w-4 h-4" /> Settings
                  </h3>
                  <div className="space-y-4">
                    {tool.fields.map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium mb-2 text-zinc-400">{field.label}</label>
                        {renderField(field)}
                        {toolId === 'file-chat' && field.name === 'file_url' && (
                          <div className="mt-3 space-y-2">
                            <label className="text-xs font-medium text-zinc-400 flex items-center gap-1">
                              <Upload className="w-3 h-3" /> Upload document from your computer
                            </label>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadDocumentFile(file);
                              }}
                              className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-violet-600 file:text-white file:text-xs hover:file:bg-violet-500"
                            />
                            {fileUploadLoading && (
                              <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" /> Uploading document...
                              </p>
                            )}
                            {formData.file_url && (
                              <div className="mt-1 text-xs text-zinc-400">
                                Using file URL: <span className="break-all text-zinc-300">{formData.file_url}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Chat Area */}
            <div className={`${tool.fields.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl h-[calc(100vh-200px)] flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-zinc-500 py-20">
                      <span className="text-5xl mb-4 block">{tool.icon}</span>
                      <p>Start a conversation with {tool.name}</p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-xl p-4 ${msg.role === 'user' ? 'bg-violet-500' : 'bg-zinc-800'}`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.image && (
                          <img src={msg.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${msg.image}` : msg.image} alt="Generated" className="mt-3 rounded-lg max-w-full" />
                        )}
                        {msg.role === 'assistant' && msg.mcp && msg.mcp.servers_used?.length > 0 && (
                          <div className="mt-3 text-xs border-t border-zinc-700/50 pt-3 text-zinc-400">
                            <span className="font-semibold text-zinc-300">Sources used:</span> {msg.mcp.servers_used.join(', ')}
                            {msg.mcp.calls && msg.mcp.calls.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.mcp.calls.map((call, idx) => (
                                  <div key={idx} className="font-mono bg-black/30 px-2 py-1.5 rounded-md text-[10px] break-all border border-zinc-700/30">
                                    {call.server} <span className="text-zinc-500">→</span> {call.tool}({JSON.stringify(call.args)})
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-800 rounded-xl p-4">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
          <div className="mt-8">
            {history.length > 0 && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">Recent history</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto text-xs text-zinc-400">
                  {history.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        const content = item.output?.response || item.output?.content;
                        const image = item.output?.generated_image?.url;
                        const mcp = item.output?._mcp;
                        if (content) {
                          setChatMessages((prev) => [
                            ...prev,
                            { role: 'assistant', content, image, mcp },
                          ]);
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-zinc-800 text-left"
                    >
                      <span className="truncate max-w-[70%]">
                        {item.input.message || 'Previous conversation'}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Input */}
                <form onSubmit={handleChatSubmit} className="p-4 border-t border-zinc-800">
                  <div className="flex gap-3">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={`Message ${tool.name}...`}
                      className="flex-1 bg-zinc-800 border-zinc-700"
                      disabled={loading}
                    />
                    <Button type="submit" disabled={loading || !chatInput.trim()} className="bg-violet-500 hover:bg-violet-600">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          // Form Interface
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {tool.fields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium mb-2">{field.label}</label>
                    {renderField(field)}
                    {toolId === 'file-chat' && field.name === 'file_url' && (
                      <div className="mt-4 space-y-2">
                        <label className="text-xs font-medium text-zinc-400 flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload document from your computer
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadDocumentFile(file);
                          }}
                          className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-violet-600 file:text-white file:text-xs hover:file:bg-violet-500"
                        />
                        {fileUploadLoading && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Uploading document...
                          </p>
                        )}
                        {formData.file_url && (
                          <div className="mt-2 text-xs text-zinc-400">
                            Using file URL: <span className="break-all text-zinc-300">{formData.file_url}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {['photo-studio', 'vision'].includes(toolId) && field.name === 'image_url' && (
                      <div className="mt-4 space-y-2">
                        <label className="text-xs font-medium text-zinc-400 flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload image from your computer
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadPhotoStudioImage(file);
                          }}
                          className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-violet-600 file:text-white file:text-xs hover:file:bg-violet-500"
                        />
                        {photoStudioUploadLoading && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Uploading image...
                          </p>
                        )}
                        {formData.image_url && (
                          <div className="mt-2 text-xs text-zinc-400">
                            Using image URL: <span className="break-all text-zinc-300">{formData.image_url}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {toolId === 'speech-to-text' && field.name === 'audio_url' && (
                      <div className="mt-4 space-y-2">
                        <label className="text-xs font-medium text-zinc-400 flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload audio from your computer
                        </label>
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadAudioFile(file);
                          }}
                          className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-violet-600 file:text-white file:text-xs hover:file:bg-violet-500"
                        />
                        {audioUploadLoading && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Uploading audio...
                          </p>
                        )}
                        {formData.audio_url && (
                          <div className="mt-2 text-xs text-zinc-400">
                            Using audio URL: <span className="break-all text-zinc-300">{formData.audio_url}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                <Button
                  type="submit"
                  disabled={loading || isAddonLocked}
                  className="w-full bg-violet-500 hover:bg-violet-600"
                >
                  {isAddonLocked ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock add-on
                    </>
                  ) : loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </form>
              
              {history.length > 0 && (
                <div className="mt-8 border-t border-zinc-800 pt-4">
                  <h4 className="text-sm font-semibold mb-3">Recent history</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto text-xs text-zinc-400">
                    {history.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setResult(item.output)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-zinc-800 text-left"
                      >
                        <span className="truncate max-w-[70%]">
                          {item.input.topic || item.input.video_topic || item.input.prompt || item.input.content || item.input.text || item.input.audio_url || item.input.product_description || item.input.description || item.input.brand_name || item.input.name || 'Previous run'}
                        </span>
                        <span className="text-[10px] text-zinc-500">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Result */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">Result</h3>
              
              {loading && (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader2 className="w-10 h-10 text-violet-400 animate-spin mb-4" />
                  <p className="text-zinc-400">Generating...</p>
                </div>
              )}

              {!loading && !result && (
                <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
                  <Sparkles className="w-10 h-10 mb-4 opacity-50" />
                  <p>Results will appear here</p>
                </div>
              )}

              {!loading && result && renderResult()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIToolPage;
